import { AggregationFunction, IAggregationsParams, IDataReaderClient, IDataRowsParams } from "@multiweb/clients/dataReader";
import { ISectionLogger } from "@multiweb/logging";
import { IAggregationsResult, IAttrValue, IPublicAttributeInfo, IRowData, OWDataType } from "@multiweb/types/contracts";
import { HeadersBuilder } from "Global/Base/Comunication/HeadersBuilder";
import { UrlQueryBuilder } from "Global/Base/Comunication/UrlQueryBuilder";
import { UrlQueryOrHeaderBuilder } from "Global/Base/Comunication/UrlQueryOrHeaderBuilder";
import { AttrStringValue, AttrValue } from "Global/Base/DataTypes/AttrValue";
import { BigNumberProxy } from "Global/Base/DataTypes/BigNumber";
import { EncAttrValue } from "Global/Base/DataTypes/EncAttrValue";
import { IBigNumber } from "Global/Base/DataTypes/IBigNumber";
import { ASServices, getServiceURL } from "OW.Remoting/Basic/ServiceEndpoints";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { SerializeFuncs } from "OW.Remoting/Remoting/Serializers";
import { EMPTY_REQUEST_CONTEXT, RequestContext } from "../../../RequestContext";
import { BaseClient } from "../BaseClient";

interface IGetDataRowsWithParamsResult {
    AttributesInfo: { Name: string; Type: string }[];
    IsVersioned: string;
    UsedGrouping: string;
    ClassName: string;
    Rows: [{
        IsEncrypted?: boolean;
        IsFinallyEncrypted?: boolean;
        Attrs?: IRowDataDTO;
        ShortenedAttrs?: string[];//Obsahuje pole názvů atributů, jejichž hodnoty byly na serveru zkráceny.
    }];
}

interface IRowDataDTO {
    ID: any; // hodnota atributu ID, která se vrátila z data readeru
    [name: string]: any;
}

interface IEncAttrValueDTO {
    encValue: string;
}

class IEncAttrValueDTO {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance === "object" && Reflect.has(instance, "encValue");
    }
}

/**
 * @internal
 */
export class DataReaderClient extends BaseClient implements IDataReaderClient {
    private readonly _logger: ISectionLogger;

    constructor(communicationManager: ICommunicationManager, logger: ISectionLogger) {
        super(communicationManager);
        this._logger = logger;
    }

    public getAggregation(aggregationFunction: AggregationFunction, className: string, attributeName: string, condition: string, conditionParams: any[] = null, readVersioned: boolean = false, requestContext?: RequestContext): Promise<IBigNumber> {
        const aggregationAttribute = createAggregationAttribute(aggregationFunction, attributeName);
        return this.getDataRowsWithParams(className, [aggregationAttribute], condition, { params: conditionParams, readVersioned }, undefined, requestContext)
            .then(createConvertAggregationResultToBigNumber(aggregationAttribute));
    }

    public getAggregations(aggregationParams: IAggregationsParams, className: string, condition: string, conditionParams?: readonly any[], readVersioned?: boolean, requestContext?: RequestContext): Promise<IAggregationsResult> {
        const aggregationAttributes = Object.values(aggregationParams).map(({ aggregationFunction, attributeName }) => createAggregationAttribute(aggregationFunction, attributeName));
        return this.getDataRowsWithParams(className, aggregationAttributes, condition, { params: conditionParams, readVersioned }, undefined, requestContext)
            .then(createMapAggregationsParamsToResult(aggregationParams));
    }

    public exists(className: string, condition: string, conditionParams: any[] = null, readVersioned: boolean = false, requestContext?: RequestContext): Promise<boolean> {
        return this._communicationManager.callPOSTRequest("Exists", ASServices.dataRows, { className }, { condition, parms: conditionParams, readVersioned }, undefined, requestContext);
    }

    public getCount(className: string, condition: string, conditionParams: any[] = null, readVersioned: boolean = false, requestContext?: RequestContext): Promise<number> {
        return this._communicationManager.callPOSTRequest("Count", ASServices.dataRows, { className }, { condition, parms: conditionParams, readVersioned }, undefined, requestContext);
    }

    public getDataRowsWithParams(className: string, attributes: string[], condition: string, parameters: IDataRowsParams = {}, fullDataAttributes?: string[], requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): Promise<IRowData[]> {
        return ModelDescriptionsManager.instance.getAttributesInfo(className, attributes, requestContext?.cancellationToken).then(attributeInfo => { // uz by mělo být v keši
            // pokud má atribut ve své description, že se má zobrazovat nějaký jeho interní atribut, tak se pro něj vygeneruje potřebný název
            attributes = attributeInfo.map((a) => a.displayAttributeName ? `${a.attributeName}.${a.displayAttributeName}` : a.attributeName); //`

            const paramsForRequest = createParamsForRequest(parameters.params);
            const { cancellationToken } = requestContext;

            //#region setParameters
            const urlQuery = new UrlQueryBuilder();
            const headers = new HeadersBuilder({ "Accept": "application/vnd.tescosw.ow-datarows+json" });
            const mixed = new UrlQueryOrHeaderBuilder(urlQuery, headers, { maxQSParamLenght: 750 });
            const falseIsDefault = (v: any) => v == false;

            mixed.set("select", attributes, null, false);
            mixed.set("filter", condition);

            urlQuery.set("filterParams", (paramsForRequest || []).map((v) => JSON.stringify(v)), (v) => !Array.isArray(v) || v.length === 0);
            urlQuery.set("orderBy", parameters.orderBy);
            urlQuery.set("groupBy", parameters.groupBy);
            urlQuery.set("distinct", parameters.distinct, falseIsDefault);

            headers.set("X-TSC-maxStringsLength", parameters.dataLength === -1 ? undefined : parameters.dataLength || 0);
            headers.set("X-TSC-fullLengthAttrs", fullDataAttributes?.join(","));
            headers.set("X-TSC-readVersioned", parameters.readVersioned, falseIsDefault);
            const startIndex = parameters.startIndex || 0;
            if (startIndex > 0 || parameters.count)
                headers.set("Range", `rows=${startIndex}-${!parameters.count || parameters.count < 0 ? "" : startIndex + parameters.count - 1}`);
            //#endregion

            return this._communicationManager.jsonRequest(`${className}?${urlQuery}`, undefined, "GET", getServiceURL(ASServices.data), headers, requestContext)
                .then(async (msg: IGetDataRowsWithParamsResult) => {
                    cancellationToken?.throwIfCancellationRequested();

                    const desc = new Array<Promise<any>>();
                    const descMap = new Map<string, { description: IPublicAttributeInfo; defaultValue: any; deserializer: ((value: any) => any) }>();
                    const aliases = new Map<string, string>();
                    const encAttrs = new Array<string>();

                    for (const ai of attributeInfo) {
                        const enumName = ai.type === OWDataType.enum ? ai.additionalTypeName : undefined;
                        // vytvoření názvu atributu, který přijde v datech
                        const rowAttrName = ai.displayAttributeName ? `${ai.attributeName}.${ai.displayAttributeName}` : ai.attributeName; //`
                        const rowAttrType = ai.displayAttributeName ? ai.displayAttributeType : ai.type;
                        if (ai.displayAttributeName)
                            aliases.set(rowAttrName, ai.attributeName); // pro rychlejší vyhledání se vytvoří mapování získaného atributu na atribut použitý navenek

                        const deserializerPromise = SerializeFuncs.getTypeSerializers(rowAttrType, enumName)
                            .then((deser) => {
                                const { attrName, defaultValue } = SerializeFuncs.getTypeDescription(rowAttrType, rowAttrName, enumName);
                                descMap.set(attrName, {
                                    description: ai,
                                    defaultValue,
                                    deserializer: deser && deser.deserializer ? deser.deserializer : undefined,
                                });
                            })
                            .catch(e => this._logger.error("Unable to create cell data deserializer", e));

                        desc.push(deserializerPromise);

                        if (ai.encryptingAlowed) {
                            encAttrs.push(rowAttrName);
                        }
                    }

                    // spusti se az jsou vsechny deserializery hotove
                    await Promise.all(desc);

                    cancellationToken.throwIfCancellationRequested();

                    const rows: IRowData[] = [];
                    for (const row of msg.Rows) {
                        const newRow: IRowData = {} as IRowData;
                        const rowAttrs = row.Attrs;
                        if (rowAttrs) {
                            for (const attr in rowAttrs) {
                                if (Object.prototype.hasOwnProperty.call(rowAttrs, attr)) {
                                    const val = rowAttrs[attr];
                                    const realAttrName = aliases.get(attr); // interní atributy se přemapují na atributy použité navenek
                                    const desc = descMap.get(attr);

                                    let attrVal: IAttrValue;
                                    if (val instanceof IEncAttrValueDTO) { // pokud je hodnota atributu šifrovaná, tak se pro ní vytvoří i šifrovaná reprezentace na klientovi
                                        attrVal = new EncAttrValue(val.encValue, !desc.description.supportsEncValue, desc.defaultValue, desc.deserializer);
                                    } else {
                                        if (desc && (desc.description.type === OWDataType.string)) {
                                            const isShortened = row.ShortenedAttrs && row.ShortenedAttrs.some((a) => a === attr);//Pro řetězcové datové typy vytváříme AttrStringValue s příznakem, zda došlo ke zkrácení hodnot.
                                            attrVal = new AttrStringValue(desc && desc.deserializer ? desc.deserializer(val) : val, isShortened);
                                        } else
                                            attrVal = new AttrValue(desc && desc.deserializer ? desc.deserializer(val) : val);
                                    }
                                    newRow[realAttrName ? realAttrName : attr] = attrVal;
                                }
                            }
                        }

                        if (row.IsEncrypted) {
                            // přidá prázdné šifrované atributy, aby se na jejich základě mohla pole zobrazovat jako zašifrovaná a ne prázdná (to uživatel neví)
                            for (const encAttr of encAttrs)
                                if (!newRow[encAttr])
                                    newRow[encAttr] = new EncAttrValue(null, false, descMap.get(encAttr).defaultValue, null);
                        }

                        if (row.IsFinallyEncrypted)
                            newRow["IsFinallyEncrypted"] = true;

                        rows.push(newRow);
                    }
                    return rows;
                });
        });
    }
}

/**
 * Provede mapování parametrů, kde nahradí OWObject za jeho id. Pokud položka není OWObject, zůstane nezměněna.
 * @param params Parametry k mapování.
 */
const createParamsForRequest = (params: readonly any[] = null) => {
    if (params) {
        return params.map((param) => param instanceof IOWObject ? param.id : param);
    } else {
        return params;
    }
};

/**
 * Vytvoří funkci pro převod výsledku z volání getDataRowsWithParams na výsledek agregační funkce.
 * @param aggregationAttribute Název atributu, pro který se získává výsledek.
 * @throws Vyhazuje výjimku v případě, kdy výsledek neobsahuje přesně jednu hodnotu.
 */
const createConvertAggregationResultToBigNumber = (aggregationAttribute: string) => (rowData: IRowData[]): IBigNumber => {
    if (rowData.length === 1) {
        const aggregationAttrValue = AttrValueReader.readRow(rowData[0], aggregationAttribute);
        if (aggregationAttrValue === null) {
            /* Pokud pro agregovaný atribut neexistují žádné hodnoty ve filtrovaných záznamech, vrací server hodnotu null. Na klientovi není třeba tuto situaci rozlišovat
             * od výsledku s hodnotou 0. Proto se převede na hodnotu 0. Viz diskuse https://tfs.tescosw.loc/Jadro/Jadro%20.NET/_git/MultiWebClient/pullrequest/5272?discussionId=76331 */
            return new BigNumberProxy(0);
        }
        if (aggregationAttrValue instanceof BigNumberProxy) {
            return aggregationAttrValue as BigNumberProxy;
        } else {
            return new BigNumberProxy(aggregationAttrValue as number);
        }
    } else {
        throw new Error(`[DataReaderClient] Server response for aggregation function does not contain single value. Response is ${rowData}`);
    }
};

/**
 * Vytvoří agregační atribut pro funkci getDataRowsWithParams ze zadané agregační funkce a názvu atributu.
 * @param aggregationFunction
 * @param attributeName
 */
const createAggregationAttribute = (aggregationFunction: AggregationFunction, attributeName: string): string => `${aggregationFunction}(${attributeName})`;

/**
 * Namapuje výsledky agregací na klíče vstupních parametrů.
 * @param aggregationParams
 */
const createMapAggregationsParamsToResult = (aggregationParams: IAggregationsParams) => (rowData: IRowData[]): IAggregationsResult => {
    return Object.entries(aggregationParams).reduce((result, [key, { aggregationFunction, attributeName }]) => {
        const aggregationAttribute = createAggregationAttribute(aggregationFunction, attributeName);
        const aggregationResult = createConvertAggregationResultToBigNumber(aggregationAttribute)(rowData);
        return {
            ...result,
            [key]: aggregationResult,
        };
    }, {} as IAggregationsResult);
};
